import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DdexImport } from '../api/lib/schemas';
import { ConfigurationInfo } from './ConfigurationInfo';
import { DownloadDdexInfo } from './DownloadDdexInfo';
import { StatusInfo } from './StatusInfo';
import { UPCInfo } from './UPCInfo';

export const useImportsColumns = () => {
  const { t } = useTranslation('ddex-console');

  return useMemo(() => {
    const columnHelper = createColumnHelper<DdexImport>();

    return [
      columnHelper.accessor((row) => row.createdAt, {
        id: 'created_at',
        cell: (cell) => {
          const date = new Date(cell.getValue());
          return (
            <>
              {format(date, 'MMM d, yyyy')}
              <br />
              <span className="text-body-xs text-subtle">
                {formatInTimeZone(date, 'America/New_York', 'h:mm a z')}
              </span>
            </>
          );
        },
        header: () => t('syncDate'),
      }),
      columnHelper.accessor((row) => row, {
        id: 'titleArtist',
        cell: (cell) => {
          const { title, artistName } = cell.getValue();
          return (
            <>
              {title || '-'}
              <div className="text-body-xs text-subtle">
                {artistName || '-'}
              </div>
            </>
          );
        },
        header: () => t('titleArtist'),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'configuration',
        cell: (cell) => {
          const { releaseType, releaseSubtype, releaseFormat } =
            cell.getValue();

          return (
            <ConfigurationInfo
              releaseFormat={releaseFormat}
              releaseType={releaseType}
              releaseSubtype={releaseSubtype}
            />
          );
        },
        header: () => t('configuration'),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'labelFamily',
        cell: (row) => {
          const { labelName, partnerName } = row.getValue();
          return (
            <>
              {labelName || '-'}
              <div className="text-body-xs text-subtle">
                {partnerName || '-'}
              </div>
            </>
          );
        },
        header: () => t('labelFamily'),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'upc',
        cell: (cell) => {
          const { externalId, packageId } = cell.getValue();

          return <UPCInfo externalId={externalId} packageId={packageId} />;
        },
        header: () => t('upc'),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'status',
        cell: (row) => {
          const { status, statusMessage } = row.getValue();
          return <StatusInfo status={status} statusMessage={statusMessage} />;
        },
        header: () => t('importStatus'),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'id',
        cell: (cell) => {
          const { id } = cell.getValue();

          return <DownloadDdexInfo id={id} />;
        },
        header: () => t('download'),
        enableSorting: false,
      }),
    ];
  }, [t]);
};
